import type { RightWhaleState } from './types';
import { civilFromEpochDay, epochDayFromCivil } from './calendar';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';
import { CALVES_FIRST_SEEN_2026 } from './data/rightwhales/calving';

/**
 * The North Atlantic right whale (M107): the whole population, one stock,
 * which feeds off New England and Canada and calves off the southeastern
 * United States, the game's first population outside every sanctuary: it
 * belongs to the North Atlantic, its high band (40°N and north) and its low.
 * Counted by sex and year class since birth, each class aged at the calving
 * season's start; calves born to the females of calving age through the
 * season as the 2026 season's were first seen; every whale dying at its age's
 * and sex's rate. Its draws are the caller's, as the humpbacks' and the monk
 * seals' are: binomial deaths and Poisson calves on its own stream in the
 * game, expected values in its table (`scripts/rightwhale.ts`).
 *
 * **Its survival is NOAA's and its calving the one fitted number**: survival
 * by age, sex and regime from NOAA's population model of its photographs,
 * 1990-2024 (Linden 2025, NOAA Tech. Memo. NMFS-NE-338, here TM338), a year's
 * the mean over its years; the calves each female of calving age has a year
 * set so the population bears what NOAA counted over the years of that
 * regime, on the table that opens it. At those rates the game's population
 * falls 1.3% a year over its first ten seasons (`RIGHT_WHALE_DECADE`), 1.5%
 * once its ages and sexes settle (`RIGHT_WHALE_GROWTH`), where TM338's own
 * estimates fell from 483 in 2011 to 359 in 2020, the worst of the deaths in
 * 2017 to 2019, and rose to 384 in 2024 (Table 3): 1.75% a year over the
 * whole span. It is held to no count: its trend is its rates'.
 *
 * **Where it is**: each season's calves are born in the low band, off the
 * southeastern United States, and are there with their mothers to the
 * season's end; every other whale is counted in the high band, the feeding
 * grounds. Others go south too (in 2026, 83 whales besides the mothers and
 * calves, NOAA's), how many a year by class unread as a rate (open).
 */

/** A right whale's life, each figure with its source. */
export interface RightWhaleLife {
  id: 'north_atlantic_right';
  /** NOAA's estimate and the year it opens, and its females and males. */
  population: number;
  populationYear: number;
  females: number;
  males: number;
  /** TM338's survival model, logit scale: its intercept (age 0, a half-year-old), each year of age to five, an adult female, the regime after 2010. */
  survivalIntercept: number;
  survivalPerAge: number;
  survivalAgeLimit: number;
  survivalAdultFemale: number;
  survivalAfter2010: number;
  /** The spread of each year's effect on that scale (TM338's σφ(t)): a year's survival is the mean over it. */
  survivalYearSpread: number;
  /** A calf's survival over its first half year: the share of those born not lost (`bornAfter2010`, `lostAfter2010`) and TM338's further loss. */
  bornAfter2010: number;
  lostAfter2010: number;
  unseenCalfLoss: number;
  /** TM338's estimate of the whales in each of its years, 1 December to 30 November, from its first, each year's calves that recruit among them. */
  estimates: { firstYear: number; medians: readonly number[] };
  /** The age a female calves from, years, and the share of calves female. */
  calvingFromYears: number;
  femaleShare: number;
  /** The oldest class, years: it holds every whale of that age and over. */
  oldestYears: number;
  /** The calving season, [month, day] to [month, day]: classes age at its start; the calves are in the low band to its end. */
  season: { from: readonly [number, number]; to: readonly [number, number] };
  /** Each calf of a season on the day it was first seen, ISO dates: its births'. */
  firstSeen: readonly string[];
}

/**
 * The North Atlantic right whale, *Eubalaena glacialis*, each figure with its
 * source as read (M107).
 */
export const NORTH_ATLANTIC_RIGHT_WHALE: RightWhaleLife = {
  id: 'north_atlantic_right',
  /** "the estimated median population size at the start of 2024 was 384 whales, with a 95% credible interval ranging from 375 to 394" (TM338, p. 1), its year "(1 December-November 30)". */
  population: 384,
  populationYear: 2024,
  /**
   * "The 2024 estimate included 163 [157, 171] females and 221 [213, 229]
   * males" (TM338, p. 4): the table's sexes, its young alike in both and the
   * surplus of females its adults' (M107's second review: the settled ages
   * give 38.0% female, TM338's 42.4%, the sexes still diverging, "Given the
   * lower survival for adult females, patterns in sex-specific abundance
   * continue to diverge", ibid.).
   */
  females: 163,
  males: 221,
  /**
   * "logit(φi,t) = β0 + βage × Agei,t + βfemale × Sexi × Adulti,t + βregime
   * I(yeart > 2010) + εt. Here, β0 is the average survival probability for
   * Agei,t = 0 individuals (0.5 year olds); βage is the coefficient for linear
   * change in survival with ages from 1-5" (TM338, p. 3), its age classes
   * "(0,1,2,3,4,5+)" and 5+ "(adult age class)" (pp. 1-2); the posterior
   * medians of its Table 2 (p. 7): β0 3.502, βage 0.129, βfemale -0.352,
   * βregime -0.666; a year's survival the mean over the years' effects
   * (`survivalYearSpread`; each year's own not drawn: open). So a
   * half-year-old survives a year after 2010 at 0.939, a male of five and
   * over at 0.967 and a female at 0.954 (derived from the medians, not the
   * posterior of φ).
   */
  survivalIntercept: 3.502,
  survivalPerAge: 0.129,
  survivalAgeLimit: 5,
  survivalAdultFemale: -0.352,
  survivalAfter2010: -0.666,
  /**
   * Table 2's "standard deviation of temporal variation in survival
   * probability (σφ(t))", median 0.471: the game's year is the mean of the
   * years (M107's review: each year's effect set at 0 overstated an average
   * year's survival on the logit's concave side).
   */
  survivalYearSpread: 0.471,
  /**
   * TM338, Table 1 (p. 6), "Count of observed North Atlantic right whale
   * calving events associated with known mothers, including losses due to
   * observed mortality events (either calf or mother) occurring in the first
   * 6 months": 2011 to 2024, 22, 7, 20, 11, 17, 14, 5, 0, 7, 10, 20, 15, 12
   * and 19 born, 179 (summed here), 2, 1, 0, 1, 0, 2, 0, 0, 0, 2, 1, 2, 1 and
   * 5 lost, 17; and "The probability of unobserved calf mortality (κ) in the
   * first 6 months was estimated at 0.022 [0.001, 0.076]" (p. 4). A calf
   * survives its first half year at (1 - 17/179)(1 - 0.022) = 0.885.
   */
  bornAfter2010: 179,
  lostAfter2010: 17,
  unseenCalfLoss: 0.022,
  /**
   * TM338, Table 3 (p. 8), "Posterior summaries of estimated population sizes
   * from multistate capture-recapture model of North Atlantic right whales,
   * including an integration of known calf counts, from 1990-2024", its
   * medians: each year's whales, its calves that recruit among them ("For the
   * terminal year in 2024, there were 14 calves included as potential
   * recruits to the population", p. 1). 2011 to 2024 make 5,913 whale-years.
   */
  estimates: {
    firstYear: 1990,
    medians: [289, 298, 305, 299, 303, 305, 320, 329, 329, 328, 319, 346, 355, 365, 372, 396, 401, 413, 430, 466, 476, 483, 473, 481, 477, 473, 457, 434, 390, 381, 359, 371, 374, 376, 384],
  },
  /**
   * "Female right whales become sexually mature at about age 10" (NOAA
   * Fisheries' calving page for 2026 and its species page): the 2026
   * season's youngest mothers, #4610 and #4617, were 10, born in 2016 (ibid.).
   * Calves are taken as half female (assumed: TM338's 0.463 female is the
   * population's, `πfemale`, not its calves').
   */
  calvingFromYears: 10,
  femaleShare: 0.5,
  /**
   * "In the northern hemisphere, right whales can live to at least 70 years"
   * (NOAA Fisheries' species page): the classes run to 70, the last holding
   * every whale of 70 and over, since TM338's survival has no old age and the
   * page's shorter lives "are due to human-caused mortality, not old age"
   * (M107's review: killed at 70, the last class's 2.6 whales of 384 a year
   * made 0.67 points of the fall).
   */
  oldestYears: 70,
  /** "The right whale calving season begins in mid-November and runs through mid-April" (NOAA Fisheries' calving page for 2026): 15 November to 15 April. */
  season: { from: [11, 15], to: [4, 15] },
  /** The 2026 season's 23 calves on the days each was first seen with its mother (`data/rightwhales/calving.ts`, from NOAA's page). */
  firstSeen: CALVES_FIRST_SEEN_2026,
};

/** What the table sets (`data/rightwhales/`): the calves a female of calving age has a year. */
export interface RightWhaleSupply {
  calvesPerFemale: number;
}

/** A calf's first span, years: the half year TM338's calf loss is over. */
const CALF_YEARS = 0.5;
/** The days before its first sighting a calf is taken as born over, the sighting's day the last (a choice: "less than 4 days old" to "a month old or less" on NOAA's page). */
const BIN_DAYS = 7;

/** The classes: 0 to `oldestYears`. */
export const rightWhaleClasses = (life: RightWhaleLife): number => life.oldestYears + 1;

/** The calving season a day falls in, by the year its start is in, and the day's place in it, 0 its first day. */
export function seasonOf(life: RightWhaleLife, epochDay: number): { year: number; day: number } {
  const { year } = civilFromEpochDay(epochDay);
  const start = (y: number): number => epochDayFromCivil(y, life.season.from[0], life.season.from[1]);
  const y = epochDay >= start(year) ? year : year - 1;
  return { year: y, day: epochDay - start(y) };
}

/**
 * The season's births by its days, each calf spread over the `BIN_DAYS` days
 * ending on the day it was first seen, the latest it can have been born
 * (M107's review: by the week from the season's start, a third of them fell
 * after their sightings); and their mean day.
 */
const binned = new Map<RightWhaleLife, { shares: number[]; meanDay: number }>();
function births(life: RightWhaleLife): { shares: number[]; meanDay: number } {
  let b = binned.get(life);
  if (b === undefined) {
    const days = life.firstSeen.map((iso) => {
      const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
      return seasonOf(life, epochDayFromCivil(y, m, d)).day;
    });
    const shares = Array<number>(Math.max(...days) + 1).fill(0);
    for (const d of days) for (let k = d - BIN_DAYS + 1; k <= d; k++) shares[k] = (shares[k] as number) + 1 / days.length / BIN_DAYS;
    b = { shares, meanDay: days.reduce((a, d) => a + d, 0) / days.length - (BIN_DAYS - 1) / 2 };
    binned.set(life, b);
  }
  return b;
}

/** The share of a season's calves born on a day of it (`births`). */
export function birthShare(life: RightWhaleLife, seasonDay: number): number {
  return births(life).shares[seasonDay] ?? 0;
}

/** A class's whales' mean age in years on a day of the season: class 0 the season's calves. */
const ageOn = (life: RightWhaleLife, cls: number, seasonDay: number): number => cls + (seasonDay - births(life).meanDay) / YEAR_DAYS;

/**
 * The share of a season's calves TM338 counts as its potential recruits: those born less those seen lost ("Annual calf
 * counts (Table 1), with observed losses subtracted from the total observed births, were used to define the expected
 * number of entries", p. 2; "For the terminal year in 2024, there were 14 calves included as potential recruits to the
 * population", p. 1). NOAA's 384 counts them.
 */
export const potentialRecruitShare = (life: RightWhaleLife): number => 1 - life.lostAfter2010 / life.bornAfter2010;

/** A calf's survival over its first half year: those born not lost, and TM338's unseen loss. */
export const calfHalfYearSurvival = (life: RightWhaleLife): number => potentialRecruitShare(life) * (1 - life.unseenCalfLoss);

/** Steps either side of the mean over a year's effect, and how many spreads they reach (a choice: past 1e-9 of the exact mean, MEASURED by the test against 10 times the steps). */
const YEAR_STEPS = 200;
const YEAR_REACH = 8;

/** The logistic's mean over the years at a logit x, each year's effect normal with spread sd: Simpson's rule. */
export function meanOverYears(x: number, sd: number): number {
  const h = (YEAR_REACH * sd) / YEAR_STEPS;
  let sum = 0;
  for (let i = -YEAR_STEPS; i <= YEAR_STEPS; i++) {
    const e = i * h;
    const weight = i === -YEAR_STEPS || i === YEAR_STEPS ? 1 : (i + YEAR_STEPS) % 2 === 1 ? 4 : 2;
    sum += (weight * Math.exp(-(e * e) / (2 * sd * sd))) / (1 + Math.exp(-(x + e)));
  }
  return (sum * h) / 3 / (sd * Math.sqrt(2 * Math.PI));
}

/** A year's survival at an age from a half year on, by TM338's model after 2010: its age class (age 0 a half-year-old, 5 and over adults), an adult female's lower, the mean over the years (`survivalYearSpread`). */
export function survivalAt(life: RightWhaleLife, female: boolean, ageYears: number): number {
  const cls = Math.min(life.survivalAgeLimit, Math.max(0, Math.floor(ageYears - CALF_YEARS)));
  const adultFemale = female && cls === life.survivalAgeLimit ? life.survivalAdultFemale : 0;
  return meanOverYears(life.survivalIntercept + life.survivalPerAge * cls + adultFemale + life.survivalAfter2010, life.survivalYearSpread);
}

/** TM338's whales in its year (1 December to 30 November) that begins in a season, the first year's for any before it; undefined after its last. */
export function estimateFor(life: RightWhaleLife, season: number): number | undefined {
  const { firstYear, medians } = life.estimates;
  return medians[Math.max(0, season + 1 - firstYear)];
}

/** TM338's whale-years and the calves that recruited among them, 2011 to 2024, its regime's: the fit's denominator, its whales before the season's calves (M107's review). */
export function whaleYearsAfter2010(life: RightWhaleLife): { whaleYears: number; recruits: number; years: number } {
  const from = 2011 - life.estimates.firstYear;
  const years = life.estimates.medians.slice(from);
  // TM338's recruits are its potential recruits, its calves born less those lost (`potentialRecruitShare`): "Of the 471
  // total births during 1991-2023 that could yield a potential recruit to the population", "the total recruits during
  // 1991-2023 were estimated to be 473 [466, 479], matching the total observed births during that period" (p. 4; M107's
  // second review: its unseen loss κ taken off too).
  return { whaleYears: years.reduce((a, b) => a + b, 0), recruits: life.bornAfter2010 * potentialRecruitShare(life), years: years.length };
}

/** A whale's chance of dying in a day at its age: a calf's first half year at its half year's survival, then each age's year. */
export function dailyDeathAt(life: RightWhaleLife, female: boolean, ageYears: number): number {
  if (ageYears < CALF_YEARS) return 1 - calfHalfYearSurvival(life) ** (1 / (CALF_YEARS * YEAR_DAYS));
  return dailyDeath(survivalAt(life, female, ageYears));
}

/**
 * `dailyDeathAt` for the step, each of its 13 values worked out once (a
 * calf's half year, then each sex at each of TM338's six age classes): the
 * same numbers, without two powers a class a day (M107: the step from 16 to
 * 26 µs a day to 6 to 14, three runs of 3,650 days each, MEASURED).
 */
const deathTables = new Map<RightWhaleLife, { calf: number; female: number[]; male: number[] }>();
function dailyDeathCached(life: RightWhaleLife, female: boolean, ageYears: number): number {
  let t = deathTables.get(life);
  if (t === undefined) {
    const of = (f: boolean): number[] => Array.from({ length: life.survivalAgeLimit + 1 }, (_, cls) => dailyDeathAt(life, f, cls + CALF_YEARS));
    t = { calf: dailyDeathAt(life, false, 0), female: of(true), male: of(false) };
    deathTables.set(life, t);
  }
  if (ageYears < CALF_YEARS) return t.calf;
  return (female ? t.female : t.male)[Math.min(life.survivalAgeLimit, Math.max(0, Math.floor(ageYears - CALF_YEARS)))] as number;
}

/** On the season's first day, every class a year older, the last holding all of 70 and over; on any other day, nothing. */
function ageRightWhales(life: RightWhaleLife, w: RightWhaleState, epochDay: number): void {
  if (seasonOf(life, epochDay).day !== 0) return;
  const last = rightWhaleClasses(life) - 1;
  for (const c of [w.females, w.males]) {
    c[last] = (c[last] as number) + (c[last - 1] as number);
    for (let k = last - 1; k > 0; k--) c[k] = c[k - 1] as number;
    c[0] = 0;
  }
}

/**
 * A day of the right whales' lives, the classes aged (`ageRightWhales`): the
 * day's calves, the females of calving age at the table's rate a year, the
 * day's share of the season's births (`birthShare`), half of them female;
 * then each class of each sex dies at its mean age's rate, the day's calves
 * among them.
 */
export function stepRightWhales(life: RightWhaleLife, w: RightWhaleState, epochDay: number, supply: RightWhaleSupply, draws: PopulationDraws): void {
  ageRightWhales(life, w, epochDay);
  const { day } = seasonOf(life, epochDay);
  const share = birthShare(life, day);
  if (share > 0) {
    let mothers = 0;
    for (let k = life.calvingFromYears; k < w.females.length; k++) mothers += w.females[k] as number;
    const calves = mothers * supply.calvesPerFemale * share;
    w.females[0] = (w.females[0] as number) + draws.count(calves * life.femaleShare);
    w.males[0] = (w.males[0] as number) + draws.count(calves * (1 - life.femaleShare));
  }
  for (const [c, female] of [[w.females, true], [w.males, false]] as const) {
    for (let k = 0; k < c.length; k++) {
      const n = c[k] as number;
      if (n > 0) c[k] = n - draws.share(n, dailyDeathCached(life, female, ageOn(life, k, day)));
    }
  }
}

/** Whether a day is in the calving season, from its start to its end, both in it. */
export function inSeason(life: RightWhaleLife, epochDay: number): boolean {
  const { year } = seasonOf(life, epochDay);
  return epochDay <= epochDayFromCivil(year + 1, life.season.to[0], life.season.to[1]);
}

/** What the map reads on a day: the population by sex, the season's calves, and those in the low band (the calves and their mothers in the season) and the high. */
export interface RightWhaleCounts {
  population: number;
  females: number;
  males: number;
  calves: number;
  low: number;
  high: number;
}
export function rightWhaleCounts(life: RightWhaleLife, w: RightWhaleState, epochDay: number): RightWhaleCounts {
  const females = w.females.reduce((a, b) => a + b, 0);
  const males = w.males.reduce((a, b) => a + b, 0);
  const calves = (w.females[0] as number) + (w.males[0] as number);
  const low = inSeason(life, epochDay) ? 2 * calves : 0;
  return { population: females + males, females, males, calves, low, high: females + males - low };
}

export function emptyRightWhales(life: RightWhaleLife): RightWhaleState {
  return { females: Array<number>(rightWhaleClasses(life)).fill(0), males: Array<number>(rightWhaleClasses(life)).fill(0) };
}
